/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useErrorBoundary } from 'react-error-boundary';
import {
  Typography,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  MenuItem,
  Box,
  IconButton
} from '@mui/material';
import SquareIcon from '@mui/icons-material/Square';
import CircleIcon from '@mui/icons-material/Circle';
import CloseIcon from '@mui/icons-material/Close';
import Dayjs from 'dayjs';

import useSsurgoLabel from '@hooks/useSsurgoLabel';
import { useAppTheme } from '@hooks/useAppTheme';
import { RootState } from '@state-mgmt/store';
import {
  PreviousVisitSelection,
  selectFieldVisitWizardMapStyle,
  selectImagery,
  selectImageryEnabled,
  selectSelectedImage,
  setFieldVisitWizardMapStyle,
  setImageryEnabled,
  setPreviousVisitsSelection,
  setSelectedImage
} from '@state-mgmt/slices/visitSlice';
import { styles } from './styles';
import { useTranslation } from 'react-i18next';
import { mapStyles } from '@components/MapView/MapView';
import { SwipeableDrawer } from '@mui/material';
import { THEME_MODE } from '@state-mgmt/slices/appSlice';

export enum MAP_LAYERS {
  TRIAL_AREA = 'trial-area',
  TREATMENT_ZONES = 'treatment-zones',
  SURGO = 'surgo'
}

export interface Props {
  treatmentZones: { color: string; label: string }[];
  selectedLayers: string[];
  hasTreatmentZone?: boolean;
  onMapLayersChange: (mapLayers: string[]) => void;
  onClose: () => void;
}

const FieldMapOptionsModal = ({ treatmentZones, selectedLayers, hasTreatmentZone = false, onMapLayersChange, onClose }: Props) => {
  const [open, setOpen] = useState<boolean>(false);
  const { t } = useTranslation();
  const { showBoundary } = useErrorBoundary();
  const theme = useAppTheme({});
  const dispatch = useDispatch();
  const { themeMode } = useSelector((state: RootState) => state.app);
  const { previousVisitsSelection } = useSelector((state: RootState) => state.visit);
  const imageryStatus = useSelector(selectImagery);
  const imageryEnabled = useSelector(selectImageryEnabled);
  const selectedImage = useSelector(selectSelectedImage);

  const ssurgoLabel = useSsurgoLabel();
  const [controlledValue, setControlledValue] = useState<string[]>(selectedLayers || []);
  const [controlledPreviuosVisits, setControlledPreviuosVisits] = useState<PreviousVisitSelection[]>(previousVisitsSelection);

  useEffect(() => {
    setOpen(true);
  }, []);

  useEffect(() => {
    onMapLayersChange(controlledValue);
  }, [controlledValue]);

  const handleOnMapLayersChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const value = event.target.value as string;
      controlledValue.includes(value)
        ? setControlledValue(controlledValue.filter((mapLayer: string) => mapLayer !== value))
        : setControlledValue([...controlledValue, value]);
    } catch (error) {
      showBoundary(error);
    }
  };

  const handleOnPreviousVisitSelectionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const value = event.target.value as string;
      const result = controlledPreviuosVisits.map(previousVisit =>
        previousVisit.id === value ? { ...previousVisit, selected: !previousVisit.selected } : previousVisit
      );
      setControlledPreviuosVisits(result);
    } catch (error) {
      showBoundary(error);
    }
  };

  useEffect(() => {
    dispatch(setPreviousVisitsSelection(controlledPreviuosVisits));
  }, [controlledPreviuosVisits]);

  const mapStyle = useSelector(selectFieldVisitWizardMapStyle);
  const onMapStyleChange = (e: SelectChangeEvent) => {
    dispatch(setFieldVisitWizardMapStyle(e.target.value));
  };

  const handleChangeImageryEnabled = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      dispatch(setImageryEnabled(checked));
    },
    [dispatch]
  );

  const onSelectedImageChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      dispatch(setSelectedImage(event.target.value));
    },
    [dispatch]
  );

  const onDrawerClose = () => {
    setOpen(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  return (
    <SwipeableDrawer
      sx={{}}
      anchor="bottom"
      open={open}
      onClose={onDrawerClose}
      onOpen={() => {}}
      disableDiscovery={true}
      disableSwipeToOpen={true}
      hideBackdrop
      disableEnforceFocus
      disableRestoreFocus
      disableScrollLock
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '65vh',
          minHeight: '50vh',
          pointerEvents: 'auto',
          mx: 'auto',
          backgroundColor: themeMode === THEME_MODE.DARK ? '#1F2128' : '#fff'
        }
      }}
      ModalProps={{
        BackdropProps: {
          invisible: true
        },
        sx: {
          pointerEvents: 'none'
        }
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          paddingTop: 2
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '25%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 40,
            height: 4,
            bgcolor: 'text.disabled',
            borderRadius: 2
          }}
        />

        <Typography variant="subtitle1">{t('map-options.title')}</Typography>

        <IconButton onClick={onDrawerClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <div id="map-options-modal-container" css={styles.scrollableArea(theme)}>
        <Card variant="outlined" css={styles.card(theme)}>
          <CardContent>
            <Typography variant="h6" component="div">
              {t('map-options.layers-title')}
            </Typography>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <FormControl size="small" fullWidth sx={{ marginTop: 1, fieldset: { legend: { maxWidth: '100%' } } }}>
                <InputLabel variant="outlined" htmlFor="field-input" shrink={true}>
                  {t('map-options.base-layer')}
                </InputLabel>

                <Select id="field-input" value={mapStyle || mapStyles.satellite} label={t('map-options.base-layer')} onChange={onMapStyleChange}>
                  <MenuItem value={mapStyles.satellite}>{t('map-options.base-layer-satellite')}</MenuItem>
                  <MenuItem value={mapStyles.dark}>{t('map-options.base-layer-dark')}</MenuItem>
                  <MenuItem value={mapStyles.light}>{t('map-options.base-layer-light')}</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox value={MAP_LAYERS.TRIAL_AREA} checked={controlledValue.includes(MAP_LAYERS.TRIAL_AREA)} onChange={handleOnMapLayersChange} />
                }
                label={<Typography sx={{ fontSize: '14px' }}>{t('map-options.trial-area-label')}</Typography>}
              />
              {hasTreatmentZone ? (
                <FormControlLabel
                  control={
                    <Checkbox
                      value={MAP_LAYERS.TREATMENT_ZONES}
                      checked={controlledValue.includes(MAP_LAYERS.TREATMENT_ZONES)}
                      onChange={handleOnMapLayersChange}
                    />
                  }
                  label={<Typography sx={{ fontSize: '14px' }}>{t('map-options.treatment-zones-label')}</Typography>}
                />
              ) : null}

              <FormControlLabel
                control={<Checkbox value="surgo" checked={controlledValue.includes('surgo')} onChange={handleOnMapLayersChange} />}
                label={<Typography sx={{ fontSize: '14px' }}>{ssurgoLabel}</Typography>}
              />

              {controlledPreviuosVisits.map(visit => (
                <FormControlLabel
                  control={<Checkbox value={visit.id} checked={visit.selected} onChange={handleOnPreviousVisitSelectionChange} />}
                  label={
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <CircleIcon
                        style={{
                          color: visit.color,
                          fontSize: '18px',
                          zIndex: 930
                        }}
                      />
                      <Typography sx={{ fontSize: '14px' }}>
                        {visit.label} ({Dayjs(visit.completedAt).format('MM/DD/YYYY')})
                      </Typography>
                    </div>
                  }
                />
              ))}

              {imageryStatus?.length ? (
                <FormControlLabel
                  control={<Checkbox checked={imageryEnabled} onChange={handleChangeImageryEnabled} />}
                  label={<Typography sx={{ fontSize: '14px' }}>{t('map-options.imagery')}</Typography>}
                />
              ) : null}

              {imageryStatus?.length ? (
                <FormControl size="small" fullWidth sx={{ marginTop: 1, fieldset: { legend: { maxWidth: '100%' } } }} disabled={!imageryEnabled}>
                  <InputLabel variant="outlined" htmlFor="image-date-input" shrink={true}>
                    {t('map-options.image-date')}
                  </InputLabel>

                  <Select id="image-date-input" value={selectedImage || ''} label={t('map-options.image-date')} onChange={onSelectedImageChange}>
                    {imageryStatus.map(status => {
                      return (
                        <MenuItem value={status.id || ''} key={status.id || ''}>
                          {Dayjs(status.date).format('MM/DD/YYYY')}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              ) : null}
            </div>
          </CardContent>
        </Card>

        {treatmentZones.length > 0 && (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" component="div">
                {t('map-options.treatment-zones-label')}
              </Typography>

              <div css={styles.treatmentZonesContainer}>
                {treatmentZones.map(treatmentZone => (
                  <div css={styles.treatmentZone} key={treatmentZone.color}>
                    <SquareIcon css={styles.treatmentZoneBullet(treatmentZone.color)} />
                    <Typography css={styles.treatmentZoneLabel}>{treatmentZone.label}</Typography>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </SwipeableDrawer>
  );
};

export default FieldMapOptionsModal;
